import React from 'react';
import { Badge } from '@/components/ui/badge';
import { BlurredMedia } from '@/components/BlurredMedia';
import { getImageUrl } from '@/lib/utils/image';
import { Shield } from 'lucide-react';

type GalleryItem = {
  id: number | string;
  url: string;
  thumbnail_url?: string;
  is_fsk18?: boolean;
  is_profile_image?: boolean;
  should_blur?: boolean;
};

interface ProfileGalleryGridProps {
  characterName: string;
  galleryImages: GalleryItem[];
  characterId?: number;
  t: any;
  onImageClick: (imageUrl: string, isFsk18: boolean, originalIndex: number) => void;
}

export const ProfileGalleryGrid: React.FC<ProfileGalleryGridProps> = ({
  characterName,
  galleryImages,
  characterId,
  t,
  onImageClick,
}) => {
  type Item = GalleryItem | null;

  let displayImages: Item[] = [];
  if (galleryImages.length === 1) {
    displayImages = [null, galleryImages[0] ?? null, null];
  } else if (galleryImages.length === 2) {
    displayImages = [null, galleryImages[0] ?? null, galleryImages[1] ?? null];
  } else if (galleryImages.length >= 3) {
    displayImages = [galleryImages[2] ?? null, galleryImages[0] ?? null, galleryImages[1] ?? null];
  }

  return (
    <div className="gallery-grid">
      {displayImages.map((image, displayIndex: number) => {
        if (!image) {
          return (
            <div 
              key={`placeholder-${displayIndex}`}
              className="gallery-grid-item bg-muted/50 flex items-center justify-center"
            >
              <div className="text-muted-foreground text-center">
                <div className="w-16 h-16 bg-muted/50 rounded-token-button mx-auto mb-token-sm flex items-center justify-center">
                  <span className="text-2xl">📷</span>
                </div>
                <p className="text-xs">{t('profile.noImage')}</p>
              </div>
            </div>
          );
        }

        const originalIndex = galleryImages.findIndex((img) => img?.id === image.id);

        return (
          <div 
            key={image.id}
            className="gallery-grid-item group cursor-pointer"
            onClick={() => onImageClick(getImageUrl(image.url), Boolean(image.is_fsk18), originalIndex)}
          >
            <div className="image-container">
              <BlurredMedia
                src={getImageUrl(image.url)}
                alt={t('profile.galleryImageAlt', {name: characterName})}
                shouldBlur={Boolean(image.should_blur)}
                isFsk18={Boolean(image.is_fsk18)}
                characterId={characterId}
                containerClassName="w-full h-full"
                className="w-full h-full object-cover object-top"
                autoPlayVideo
                showMuteToggle
                {...(image.thumbnail_url ? { poster: getImageUrl(image.thumbnail_url) } : {})}
              />
            </div>

            {/* FSK18 Badge - styled like CharacterCard */}
            {image.is_fsk18 && (
              <Badge
                className="absolute top-token-sm right-token-sm font-bold text-xs uppercase tracking-wide px-token-xs py-token-xs shadow-token-card flex items-center"
                style={{
                  background: 'linear-gradient(135deg, var(--color-error) 0%, var(--color-error-dark) 100%)',
                  color: 'white',
                  border: '1px solid var(--color-white-30)',
                  boxShadow: '0 2px 8px rgba(var(--color-error-rgb), 0.4)'
                }}
              >
                <Shield className="h-3 w-3 mr-token-xs" />
                {t('profile.fsk18Short')}
              </Badge>
            )}

            {image.is_profile_image && (
              <Badge className="absolute top-token-sm left-token-sm bg-warning text-xs">
                {/* Icon wird hier nicht zwingend benötigt; Konsistenz mit Altansicht */}
                {t('profile.profile')}
              </Badge>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ProfileGalleryGrid;


